// ─────────────────────────────────────────────────────────────
// inwardReport.config.js — Report 10: inward list report
// Shape: list (landscape)
// Data:  GET /api/tape/inward?vendor_id=&from_date=&to_date=
//
// Goods and charges are derived per row: the header stores the
// landed grand total, GST and the three charge columns, but no
// goods column — goods is what is left after the other two.
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export const inwardReportConfig = (inwards, filters) => {
  const list = inwards || [];
  const f = filters || {};

  const rows = list.map((i) => {
    const charges =
      num(i.freight_charges) + num(i.labour_charges) + num(i.other_charges);
    const gst = num(i.gst_amount);
    const grand = num(i.grand_total);
    return {
      bill_number: i.bill_number,
      received_date: i.received_date,
      po_number: i.po_number,
      vendor_name: i.vendor_name,
      roll_count: i.roll_count,
      total_meters: i.total_meters,
      goods: grand - gst - charges,
      gst_amount: gst,
      charges,
      grand_total: grand,
    };
  });

  const sum = (key) => rows.reduce((s, r) => s + num(r[key]), 0);

  const goods = sum('goods');
  const gst = sum('gst_amount');
  const charges = sum('charges');
  const grand = sum('grand_total');
  const meters = sum('total_meters');
  const rollCount = sum('roll_count');

  return {
    kind: 'list',
    orientation: 'l',
    title: 'Inward Report',
    subtitle: `${FORMATTERS.date(f.from_date)}  to  ${FORMATTERS.date(f.to_date)}`,

    boxes: [
      {
        heading: 'Report Filters',
        rows: [
          { label: 'Vendor', value: f.vendor_name || 'All Vendors' },
          { label: 'From Date', value: FORMATTERS.date(f.from_date) },
          { label: 'To Date', value: FORMATTERS.date(f.to_date) },
          { label: 'Receipts', value: rows.length },
        ],
      },
      {
        heading: 'Summary',
        rows: [
          {
            label: 'Rolls Received',
            value: FORMATTERS.int(rollCount),
            label2: 'Total Meters',
            value2: FORMATTERS.meters(meters),
          },
          {
            label: 'Goods Value',
            value: FORMATTERS.money(goods),
            label2: 'GST',
            value2: FORMATTERS.money(gst),
          },
          {
            label: 'Charges',
            value: FORMATTERS.money(charges),
            label2: 'Avg Cost / m',
            value2: meters > 0 ? FORMATTERS.money2(grand / meters) : '—',
          },
          { label: 'Landed Total', value: FORMATTERS.money(grand) },
        ],
      },
    ],

    columns: [
      { key: 'bill_number', label: 'Bill #', align: 'left', fmt: 'text', width: 26 },
      { key: 'received_date', label: 'Date', align: 'left', fmt: 'date', width: 24 },
      { key: 'po_number', label: 'PO #', align: 'left', fmt: 'text', width: 20 },
      { key: 'vendor_name', label: 'Vendor', align: 'left', fmt: 'text' },
      { key: 'roll_count', label: 'Rolls', align: 'right', fmt: 'int', width: 16 },
      { key: 'total_meters', label: 'Meters', align: 'right', fmt: 'meters', width: 24 },
      { key: 'goods', label: 'Goods', align: 'right', fmt: 'money', width: 26 },
      { key: 'gst_amount', label: 'GST', align: 'right', fmt: 'money', width: 22 },
      { key: 'charges', label: 'Charges', align: 'right', fmt: 'money', width: 22 },
      { key: 'grand_total', label: 'Landed', align: 'right', fmt: 'money', width: 28, bold: true },
    ],

    rows,

    columnTotals: [
      'roll_count',
      'total_meters',
      'goods',
      'gst_amount',
      'charges',
      'grand_total',
    ],
    totalLabel: 'TOTAL',

    totals: [
      { label: 'Goods Value', value: goods, fmt: 'money' },
      { label: 'GST', value: gst, fmt: 'money' },
      { label: 'Charges', value: charges, fmt: 'money' },
      { label: 'Landed Total', value: grand, fmt: 'money', emphasis: true, tone: 'net' },
    ],

    signatures: SIGNATURES.report,
  };
};

export default inwardReportConfig;